import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { Query } from '../../utils';
import API from '../../API';
import moment from 'moment';
import { OrderType, TaskType, TaskView, User } from 'types';

export enum InternalTaskType {
	classicDelivery = 0,
	classicReplacement = 1,
	classicClaiming = 2,
	own = 3,
	looseDelivery = 4,
	looseClaiming = 5,
	other = 6,
}

export type InternalTaskView = TaskView & {
	internalType: InternalTaskType | undefined;
	assignedUser: User | undefined;
};

const getTaskInternalType = (task: TaskView) => {
	if (task.orderType === OrderType.classic) {
		switch (task.type) {
			case TaskType.delivery:
				return InternalTaskType.classicDelivery;

			case TaskType.replacement:
				return InternalTaskType.classicReplacement;

			case TaskType.claiming:
				return InternalTaskType.classicClaiming;
		}
	}

	if (task.orderType === OrderType.own) {
		return InternalTaskType.own;
	}

	if (task.orderType === OrderType.loose) {
		switch (task.type) {
			case TaskType.delivery:
				return InternalTaskType.looseDelivery;

			case TaskType.replacement:
				throw new Error('Replacement tasks are not allowed for loose orders');

			case TaskType.claiming:
				return InternalTaskType.looseClaiming;
		}
	}

	if (task.orderType === OrderType.other) {
		return InternalTaskType.other;
	}

	return undefined;
};

export const useTasks = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const [offset, setOffset] = useState(parseInt(Query.decode(location.search)?.offset ?? '0') ?? 0);
	const [tasks, setTasks] = useState<InternalTaskView[]>();

	const fetch = async () => {
		try {
			const users = await API.users.getAll();
			const tasks = await API.tasks
				.getAll({
					after: moment().add(offset, 'days').startOf('day'),
					before: moment().add(offset, 'days').endOf('day'),
				})
				.then((tasks) =>
					tasks.map<InternalTaskView>((task) => ({
						...task,
						internalType: getTaskInternalType(task),
						assignedUser: task.assignedTo ? users.find((u) => u.id === task.assignedTo) : undefined,
					}))
				);

			tasks.sort((a, b) => {
				if (a.internalType !== b.internalType) {
					return (a.internalType ?? 7) - (b.internalType ?? 7);
				}

				if (
					a.orderType === OrderType.classic &&
					b.orderType === OrderType.classic &&
					a.order.onPlace !== b.order.onPlace
				) {
					return a.order.onPlace ? -1 : 1;
				}

				const aDate = moment(a.date);
				const bDate = moment(b.date);
				return aDate.isBefore(bDate) ? -1 : 1;
			});

			setTasks(tasks);
		} catch (e) {
			console.trace(e);
			message.error(`${e}`);
		}
	};

	useEffect(() => {
		const interval = setInterval(fetch, 1000 * 30);
		fetch();

		navigate(`?offset=${offset}`, { replace: true });

		return () => clearInterval(interval);
	}, [offset]);

	return {
		tasks,
		offset,
		setOffset,
	};
};
